import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  CMS_CONFIG,
  CmsConfig,
  CmsContent,
  CmsSearchParameters,
  CmsSearchResponse,
  CmsService,
  formatArticleBlock,
  formatVideoBlock,
} from '@domgen/dgx-fe-cms';
import {
  distinctUntilChangedDeep,
  filterNullUndefined,
} from '@domgen/dgx-fe-common';
import {
  AccordionArticleFilterCategory,
  AccordionArticleFilterItem,
} from '@domgen/dgx-fe-components/accordion';
import { ArticleBlock } from '@domgen/dgx-fe-components/content';
import { VideoBlock } from '@domgen/dgx-fe-components/media';
import { extractPageNameFromUrl } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import { ArticleSearchService } from '@self-fix/data-access';
import { CmsArticleSearchPage } from '@self-fix/util-models';
import {
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { SmartFixHubAnalyticsService } from 'libs/self-fix/data-access/src/lib/services/sf-hub-analytics/sf-hub-analytics-service';
import { AppEnvironment } from 'apps/dg-self-fix/src/app/models/environment/app-environment.type';
import { ENVIRONMENT } from '@domgen/dgx-fe-config';

export const accordionArticlesKey = makeStateKey<{ accordionArticles: string }>(
  'accordionArticles'
);

interface CategoryArticle {
  category: string;
  articles: CmsArticleSearchPage[];
}

interface ViewModel {
  categories: string[];
  categoryArticles: AccordionArticleFilterCategory;
  quantity: number;
  searchTerm?: string;
  subcategories: string[];
  subcategoryArticles: AccordionArticleFilterCategory;
}

interface ComponentState {
  accordionCategoryArticles: AccordionArticleFilterItem[];
  accordionSubcategoryArticles: AccordionArticleFilterItem[];
  categories: string[];
  path: string;
  rawArticles: CategoryArticle[];
  filteredSubcategories: string[];
  subcategories: string[];
  filteredArticles: CmsArticleSearchPage[];
}

@Injectable()
export class ArticleFilterComponentService extends ComponentStore<ComponentState> {
  private readonly rawArticles$: Observable<CategoryArticle[]> = this.select(
    (state: ComponentState) => state.rawArticles
  );

  private readonly accordionCategoryArticles$: Observable<
    AccordionArticleFilterItem[]
  > = this.select((state: ComponentState) => state.accordionCategoryArticles);

  private readonly accordionSubcategoryArticles$: Observable<
    AccordionArticleFilterItem[]
  > = this.select(
    (state: ComponentState) => state.accordionSubcategoryArticles
  );

  private readonly categories$: Observable<string[]> = this.select(
    (state: ComponentState) => state.categories
  );

  private readonly path$: Observable<string> = this.select(
    (state: ComponentState) => state.path
  ).pipe(
    filter(() => isPlatformBrowser(this.platformId)),
    filterNullUndefined(),
    distinctUntilChanged()
  );

  readonly selectedCategories$ = combineLatest([
    this.categories$,
    this.articleSearchService.selectedCategories$,
  ]).pipe(
    map(([categories, selectedCategories]) =>
      selectedCategories.length ? selectedCategories : categories
    )
  );

  readonly subcategories$: Observable<string[]> = this.select(
    (state: ComponentState) => state.subcategories
  ).pipe(map((subcategories) => [...new Set(subcategories)]));

  readonly selectedSubcategories$ = combineLatest([
    this.subcategories$,
    this.articleSearchService.selectedSubcategories$,
  ]).pipe(
    map(([subcategories, selectedSubcategories]) =>
      selectedSubcategories.length ? selectedSubcategories : subcategories
    )
  );

  readonly filteredSubcategories$ = this.select(
    (state: ComponentState) => state.filteredSubcategories
  );

  readonly filteredArticles$ = this.select(
    (state: ComponentState) => state.filteredArticles
  );

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(CMS_CONFIG) private readonly cmsConfig: CmsConfig,
    @Inject(ENVIRONMENT) private readonly environment: AppEnvironment,
    private readonly cmsService: CmsService,
    private readonly articleSearchService: ArticleSearchService,
    private readonly router: Router,
    private readonly transferState: TransferState,
    private readonly smartFixHubAnalytics: SmartFixHubAnalyticsService
  ) {
    super({
      accordionCategoryArticles: [],
      accordionSubcategoryArticles: [],
      categories: [],
      path: '',
      filteredSubcategories: [],
      rawArticles: [],
      subcategories: [],
      filteredArticles: [],
    });
  }

  readonly vm$: Observable<ViewModel> = combineLatest({
    categoryArticles: this.accordionCategoryArticles$.pipe(
      map((selectedCategoryArticles) => ({
        title: 'Category',
        items: selectedCategoryArticles,
      }))
    ),
    subcategoryArticles: this.accordionSubcategoryArticles$.pipe(
      map((accordionSubcategoryArticles) => ({
        title: 'Brand',
        items: accordionSubcategoryArticles,
      }))
    ),
    categories: this.selectedCategories$,
    searchTerm: this.articleSearchService.searchTerm$,
    subcategories: this.filteredSubcategories$,
    quantity: this.articleSearchService.quantity$,
  });

  readonly updateCategoryArticles = this.updater(
    (state: ComponentState, categoryArticles: CategoryArticle[]) => ({
      ...state,
      categoryArticles,
    })
  )(
    combineLatest([this.selectedCategories$, this.rawArticles$]).pipe(
      map(([selectedCategories, rawArticles]) =>
        rawArticles.filter((rawArticle) =>
          selectedCategories.includes(rawArticle.category)
        )
      )
    )
  );

  readonly updateAccordionCategoryArticles = this.updater(
    (
      state: ComponentState,
      accordionCategoryArticles: AccordionArticleFilterItem[]
    ) => ({
      ...state,
      accordionCategoryArticles,
    })
  )(
    this.rawArticles$.pipe(
      map((rawArticles) =>
        rawArticles.map((rawArticle) => ({
          title: rawArticle.category,
          accordionArticles: rawArticle.articles.map((accordionArticle) => ({
            id: accordionArticle['@id'],
            navigationLink: {
              href: accordionArticle['@path'].replace(
                /\/([A-Za-z-]+\/){3}/,
                ''
              ),
            },
            snippet: accordionArticle.introText ?? '',
            title: accordionArticle.articleTitle,
          })),
        }))
      )
    )
  );

  readonly updatePath = this.updater((state: ComponentState) => ({
    ...state,
    path: extractPageNameFromUrl(),
  }))();

  readonly updateCategories = this.updater(
    (state: ComponentState, categories: string[]) => ({
      ...state,
      categories,
    })
  )(
    this.path$.pipe(
      exhaustMap((path) =>
        path.includes('search')
          ? of(['videos', 'articles'])
          : this.getCategories(path)
      )
    )
  );

  readonly updateFilteredSubcategories = this.updater(
    (state: ComponentState, filteredSubcategories: string[]) => ({
      ...state,
      filteredSubcategories,
    })
  )(
    combineLatest([this.filteredArticles$, this.selectedSubcategories$]).pipe(
      map(([filteredArticles, selectedSubcategories]) => [
        ...new Set(
          filteredArticles
            .map((filteredArticle) => filteredArticle.brand?.brandName ?? '')
            .filter((brand) => selectedSubcategories.includes(brand))
        ),
      ])
    )
  );

  readonly updateSubcategories = this.updater(
    (state: ComponentState, subcategories: string[]) => ({
      ...state,
      subcategories,
    })
  );

  readonly updateAccordionSubcategoryArticles = this.updater(
    (
      state: ComponentState,
      accordionSubcategoryArticles: AccordionArticleFilterItem[]
    ) => ({
      ...state,
      accordionSubcategoryArticles,
    })
  )(
    combineLatest([this.filteredArticles$, this.subcategories$]).pipe(
      map(([articles, subcategories]: [CmsArticleSearchPage[], string[]]) => {
        const result = subcategories.reduce<AccordionArticleFilterItem[]>(
          (acc, subcategory) => {
            const accordionArticles = articles
              .filter((article) => {
                const matches = article.brand?.brandName === subcategory;

                return matches;
              })
              .map((accordionArticle) => ({
                id: accordionArticle['@id'],
                navigationLink: {
                  href: accordionArticle['@path'].replace(
                    /\/([A-Za-z-]+\/){3}/,
                    ''
                  ),
                },
                snippet: accordionArticle.introText ?? '',
                title: accordionArticle.articleTitle,
              }));
            return accordionArticles.length === 0
              ? [...acc]
              : [
                  ...acc,
                  {
                    accordionArticles,
                    title: subcategory,
                  },
                ];
          },
          []
        );
        return result;
      })
    )
  );

  readonly noResults = this.effect((noResults$: Observable<void>) =>
    noResults$.pipe(
      withLatestFrom(
        this.rawArticles$.pipe(distinctUntilChangedDeep(), skip(1))
      ),
      map(([, rawArticles]) =>
        rawArticles.map((rawArticles) => rawArticles.articles).flat()
      ),
      filter((rawArticles) => !rawArticles.length),
      tap(() =>
        this.router.navigate(['/error'], {
          replaceUrl: true,
        })
      )
    )
  )();

  readonly updateRawArticles = this.updater(
    (state: ComponentState, rawArticles: CategoryArticle[]) => ({
      ...state,
      rawArticles,
    })
  );

  readonly fetchArticles = this.effect(
    (categoryArticles$: Observable<CategoryArticle[]>) =>
      categoryArticles$.pipe(
        tap((categoryArticles) => {
          const subcategories = categoryArticles
            .map((categoryArticle) =>
              categoryArticle.articles.map(
                (article) => article.brand?.brandName ?? ''
              )
            )
            .flat();
          this.updateSubcategories(subcategories);
        }),
        tap((categoryArticles: CategoryArticle[]) => {
          this.updateRawArticles(categoryArticles);
        })
      )
  )(
    combineLatest([
      this.categories$,
      this.path$,
      this.articleSearchService.searchTerm$,
    ]).pipe(
      switchMap(([categories, path, searchTerm]) =>
        this.getArticles(path, searchTerm, categories).pipe(
          switchMap((accordionArticles) => {
            if (isPlatformServer(this.platformId)) {
              this.transferState.set(accordionArticlesKey, {
                accordionArticles: JSON.stringify(accordionArticles),
              });
            }

            return of(
              JSON.parse(
                this.transferState.get<{ accordionArticles: string }>(
                  accordionArticlesKey,
                  { accordionArticles: JSON.stringify(accordionArticles) }
                ).accordionArticles
              )
            );
          })
        )
      )
    )
  );

  readonly updateSelectedVideos = this.effect(
    (selectedVideos$: Observable<VideoBlock[]>) =>
      selectedVideos$.pipe(
        tap((selectedVideos: VideoBlock[]) => {
          this.articleSearchService.updateSelectedVideos(selectedVideos);
        })
      )
  )(
    combineLatest([
      this.filteredArticles$,
      this.selectedSubcategories$,
      this.selectedCategories$,
    ]).pipe(
      map(
        ([
          filteredArticles,
          selectedSubcategories,
          selectedCategories,
        ]): CmsArticleSearchPage[] => {
          const videoArticles = filteredArticles.filter((article) =>
            selectedSubcategories.includes(article.brand?.brandName ?? '')
          );

          const newVideoArticles = videoArticles.filter(
            (article) => article.category?.['@name'] === 'videos'
          );

          return newVideoArticles;
        }
      ),
      map((articles) => {
        const formattedVideos = formatVideoBlock(
          this.cmsConfig.cmsApi,
          articles
        );
        return formattedVideos;
      })
    )
  );

  readonly updateFilteredArticles = this.updater(
    (state: ComponentState, filteredArticles: CmsArticleSearchPage[]) => ({
      ...state,
      filteredArticles,
    })
  )(
    combineLatest([this.rawArticles$, this.selectedCategories$]).pipe(
      map(([rawArticles, selectedCategories]): CmsArticleSearchPage[] => {
        const filtered = rawArticles
          .filter((rawArticle) =>
            selectedCategories.includes(rawArticle.category)
          )
          .map((selectedCategoryArticle) => selectedCategoryArticle.articles)
          .flat();
        return filtered;
      })
    )
  );

  readonly updateSelectedArticles = this.effect(
    (selectedArticles$: Observable<ArticleBlock[]>) =>
      selectedArticles$.pipe(
        tap((selectedArticles: ArticleBlock[]) => {
          this.articleSearchService.updateArticles(selectedArticles);
        })
      )
  )(
    combineLatest([this.filteredArticles$, this.selectedSubcategories$]).pipe(
      map(
        ([filteredArticles, selectedSubcategories]): CmsArticleSearchPage[] => {
          const textArticles = filteredArticles
            .filter((article) => {
              // Check if article has a brand and if its brandName is in selectedSubcategories
              const brandMatch = selectedSubcategories.includes(
                article.brand?.brandName ?? ''
              );
              return brandMatch;
            })
            .filter((article) => {
              // Check if the article's category's @name is "articles" instead of category.name
              const categoryMatch = article.category?.['@name'] === 'articles';
              return categoryMatch;
            });

          return textArticles;
        }
      ),
      map((articles) => {
        const formattedArticles = formatArticleBlock(articles);
        return formattedArticles;
      })
    )
  );

  handleSelectedCategories(
    selectedCategoryArticles: AccordionArticleFilterItem[]
  ): void {
    const selectedCategories = selectedCategoryArticles.map(
      (selectedCategoryArticle) => selectedCategoryArticle.title
    );
    this.articleSearchService.updateSelectedCategories(selectedCategories);

    this.smartFixHubAnalytics.trackEvent({
      eventCategory: 'sf-hub',
      eventAction: 'type of search - dropdown',
      eventLabel: String(selectedCategories),
      pagename: 'claims-sf-hub-search-results',
    });
  }

  handleSelectedSubcategories(
    selectedSubcategoryArticles: AccordionArticleFilterItem[]
  ): void {
    const selectedSubcategories = selectedSubcategoryArticles.map(
      (selectedSubcategoryArticle) => selectedSubcategoryArticle.title
    );
    this.articleSearchService.updateSelectedSubcategories(
      selectedSubcategories
    );
  }

  getCategories(path: string): Observable<string[]> {
    const [categoryPath, selectedCategory] = path.split('/');

    return this.cmsService
      .search<CmsSearchResponse<CmsContent>>({
        'mgnl:template': 'dgx-self-fix:pages/self-fix-category',
        ...(categoryPath && { path: categoryPath }),
      })
      .pipe(
        map((templates: CmsSearchResponse<CmsContent>) => {
          const categoryNames = templates.results
            .map((template) => template['@name'])
            .filter(
              (templateName) =>
                templateName !== 'videos' && templateName !== 'articles'
            );
          return categoryNames;
        })
      );
  }

  getArticles(
    path: string,
    searchTerm: string | undefined,
    categoryList: string[]
  ): Observable<CategoryArticle[]> {
    // this is for search page
    if (categoryList.includes('videos') && categoryList.includes('articles')) {
      const supportedAppliances = this.environment.brandConfig.appliances;
      let applianceCode;
      if (supportedAppliances && supportedAppliances.length > 0) {
        applianceCode =
          supportedAppliances.join('|') +
          '|' +
          supportedAppliances.join(' |') +
          ' |';
      }
      const parameters: CmsSearchParameters = {
        limit: 50,
        orderBy: 'articleDate desc',
        'mgnl:template': 'dgx-self-fix:pages/self-fix-article',
      };
      if (applianceCode) {
        parameters['applianceCode'] = applianceCode;
      }
      return forkJoin(
        categoryList.reduce(
          (acc, categoryPath) => ({
            ...acc,
            [categoryPath]: this.getCategories(categoryPath).pipe(
              switchMap((categories) =>
                forkJoin(
                  categories.map((category) =>
                    this.cmsService
                      .search<CmsArticleSearchPage>({
                        ...parameters,
                        ...(searchTerm && { q: searchTerm.toString() }),
                        path: `${categoryPath}/${category}`,
                      })
                      .pipe(
                        map((accordionArticles) => accordionArticles.results)
                      )
                  )
                ).pipe(map((accordionArticles) => accordionArticles.flat()))
              )
            ),
          }),
          {}
        )
      ).pipe(
        map((articlesObject) => {
          const result = categoryList.map((categoryPath) => ({
            category: categoryPath,
            articles: articlesObject[categoryPath],
          }));
          return result;
        })
      );
    }

    return forkJoin(
      categoryList.map((category) => {
        const supportedAppliances = this.environment.brandConfig.appliances;
        let applianceCode;
        if (supportedAppliances && supportedAppliances.length > 0) {
          applianceCode =
            supportedAppliances.join('|') +
            '|' +
            supportedAppliances.join(' |') +
            ' |';
        }

        const config: CmsSearchParameters = {
          orderBy: 'articleDate desc',
          'mgnl:template': 'dgx-self-fix:pages/self-fix-article',
          limit: 50,
          ...(searchTerm && { q: searchTerm.toString() }),
          path:
            path.replace('self-fix/', '').split('/').length > 1
              ? `${path.split('/')[0]}/${category}`
              : `${path}/${category}`,
        };
        if (applianceCode) {
          config['applianceCode'] = applianceCode;
        }
        return this.cmsService.search<CmsArticleSearchPage>(config).pipe(
          map((accordionArticles) => ({
            category,
            articles: accordionArticles.results,
          }))
        );
      })
    ).pipe(
      tap(
        () =>
          path.split('/').length > 1 &&
          this.articleSearchService.updateSelectedCategories([
            path.split('/')[1],
          ])
      )
    );
  }
}
